<script lang="ts">
	import ErrorLabel from '../errorLabel/ErrorLabel.svelte'
	import FilePreview from '../fileUpload/FilePreview.svelte'
	import { InputType } from '../index'
	import type { FileType } from './fileType'
	import { fileTypes } from './fileType'
	import { _ } from '@repo/i18n'
	import { Icon, Label, ToastType, toasts } from '@repo/ui'
	import { ArrowUpTray } from '@steeze-ui/heroicons'
	import { run } from 'svelte/legacy'

	interface Props {
		name: string
		files?: FileList
		label?: string
		title?: string
		previewClass?: string
		deleteLabel?: string
		required?: boolean
		accept: FileType
		type?: InputType
		defaultThumbnail?: string
		isPreview?: boolean
		icon?: import('svelte').Snippet
		children?: import('svelte').Snippet
		maxSize?: number
		onSelected?: (file: File) => void
		onDelete?: () => void
	}

	let {
		name,
		files = $bindable(null),
		label = '',
		title = '',
		previewClass = '',
		deleteLabel = '',
		required = false,
		accept,
		type = InputType.file,
		defaultThumbnail = $bindable(''),
		isPreview = $bindable(false),
		icon,
		children,
		maxSize = 32 * 1024 * 1024, // 32MB in bytes
		onDelete,
		onSelected,
	}: Props = $props()

	let fileInput: HTMLInputElement = $state(null)
	let previewData: string = $state(null)
	let fileName: string
	let fileSize: number
	let dragging = $state(false)

	function onFileSelected(event: Event) {
		let file = (event.target as HTMLInputElement).files[0]

		if (!fileTypes[accept].allowedTypes.includes(file.type)) {
			toasts.add({
				type: ToastType.error,
				title: $_('form.upload.invalidFile.title'),
				text: $_('form.upload.invalidFile.text', {
					values: { fileType: file.type, supportedFileType: $_(fileTypes[accept].text) },
				}),
			})
			deleteFile()
			return
		}

		if (file.size > maxSize) {
			toasts.add({
				type: ToastType.error,
				title: $_('form.upload.invalidFile.title'),
				text: $_('form.upload.fileTooLarge', {
					values: { maxSize: Math.round(maxSize / (1024 * 1024)) },
				}),
			})
			deleteFile()
			return
		}

		onSelected?.(file)

		if (type === InputType.file) {
			fileName = file.name
			fileSize = Math.round(file.size / 1024)
			isPreview = true
			previewData = `${fileName} (${fileSize} kB)`
		} else if (type === InputType.image) {
			let reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = (event) => {
				isPreview = true
				previewData = String(event.target.result)
				defaultThumbnail = ''
			}
		}
	}

	function deleteFile(): void {
		isPreview = false
		fileInput.value = ''
		defaultThumbnail = ''
		files = null

		onDelete?.()
	}

	run(() => {
		if (fileInput && !files) {
			fileInput.value = ''
		}
	})
</script>

{#if label}
	<Label {name} {label} {required} />
{/if}

<div class="grid gap-3 {isPreview || files ? 'grid-cols-2' : ''}">
	{#if isPreview || files}
		<FilePreview
			{defaultThumbnail}
			{deleteLabel}
			onclick={deleteFile}
			{previewClass}
			{type}
			bind:previewData
		>
			{@render icon?.()}
		</FilePreview>
	{/if}

	<div class="relative">
		<input
			bind:this={fileInput}
			id={name}
			{name}
			class="absolute top-0 left-0 z-10 h-full w-full cursor-pointer opacity-0"
			accept={fileTypes[accept].accept}
			aria-label="{$_('form.imageUpload')} {$_('form.imageUploadBrowse')}"
			onchange={(event) => onFileSelected(event)}
			ondragenter={() => (dragging = true)}
			ondragleave={() => (dragging = false)}
			ondrop={() => (dragging = false)}
			{required}
			type="file"
			bind:files
		/>
		<div
			class="border-primary-500 relative flex h-28 flex-col items-center justify-center rounded-md border border-dashed px-2 text-center text-xs dark:bg-gray-700 {dragging
				? 'bg-yellow-50'
				: 'bg-gray-50'}"
		>
			{#if children}{@render children()}{:else}
				<div class="text-primary-500">
					<Icon src={ArrowUpTray} />
				</div>
			{/if}
			<div class="mt-1"></div>
			{#if title}
				{title}
			{/if}
			<div class="font-bold">
				{$_('form.imageUpload')}
				<span class="text-primary-500 dark:text-primary-400">
					{$_('form.imageUploadBrowse')}
				</span>
			</div>
			<div class="mt-1"></div>
		</div>
	</div>
</div>

<ErrorLabel {name} />
